import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import type { ToolDefinition, ToolExecutionContext } from '../types';

import {
    activeColorGradingFilters,
    colorGradingFilterParameter,
    findColorGradingFilterByName,
    listColorGradingFilterNames,
    loadActiveColorGradingFilters,
    unknownColorGradingFilterError,
} from './color-grading-filters';
import type { ToolConstructorArgs } from './Tool';
import { VideoStateTool } from './VideoStateTool';

import type { ColorGradingFilter } from 'js/src/client';
import type { RootState } from 'js/src/libs/RootState';
import {
    setGlobalColorGradingFilterIntensityOperation,
    setGlobalColorGradingFilterOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

// filter_name carries no static description or enum: both the allowed names and the
// per-filter "what it looks like" blurb are per-instance (the color grading filters injected
// into this agent), so they're set in extendParameters. execute() fetches on demand and
// validates, so an unconstrained string still resolves before the list has loaded.
export const setGlobalColorGradingSchema = z.object({
    filter_name: z.string().optional(),
    intensity: z
        .number()
        .min(0)
        .max(1)
        .default(1)
        .describe('Filter intensity from 0.0 (no effect) to 1.0 (full effect)'),
    remove: z
        .boolean()
        .default(false)
        .describe('Set to true to remove the current color grading filter'),
});
export type SetGlobalColorGradingArgs = z.infer<typeof setGlobalColorGradingSchema>;

interface SetGlobalColorGradingConstructorArgs extends ToolConstructorArgs {
    colorGradingFilters?: ColorGradingFilter[];
}

export class SetGlobalColorGradingTool extends VideoStateTool<
    typeof setGlobalColorGradingSchema,
    SetGlobalColorGradingConstructorArgs
> {
    protected override readonly schema = setGlobalColorGradingSchema;
    protected override readonly toolName = TOOL_NAMES.setGlobalColorGrading;
    public override readonly subagentAccess = SubagentAccess.BLOCKED;
    protected override readonly toolDescription =
        'Apply or remove a global color grading filter, affecting every scene in the video.';

    public override canRunAsync = true;
    private injectedFilters?: ColorGradingFilter[];

    public constructor(args: SetGlobalColorGradingConstructorArgs) {
        super(args);
        this.injectedFilters = args.colorGradingFilters;
    }

    // The allowed filter names, and the per-filter description blurb, are the filters injected
    // into this agent instance — the module-level schema can't see them, so inject them here.
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        Object.assign(
            parameters.properties.filter_name,
            colorGradingFilterParameter(activeColorGradingFilters(this.injectedFilters)),
        );
        return parameters;
    }

    public override getToolDisplayName(): string {
        return 'Setting global color grading';
    }

    public get statusIcon(): string {
        return 'palette';
    }

    public async execute(args: SetGlobalColorGradingArgs, context: ToolExecutionContext) {
        const { filter_name, intensity, remove } = args;

        try {
            // Get available color grading filters
            if (!this.getState) {
                return 'Error: Cannot access application state';
            }

            const activeFilters = await loadActiveColorGradingFilters(this.dispatch, this.getState);

            const { setGlobalColorGradingFilter, setGlobalColorGradingFilterIntensity } =
                makeOperationsCallable(
                    {
                        setGlobalColorGradingFilterOperation,
                        setGlobalColorGradingFilterIntensityOperation,
                    },
                    this.luminary,
                    this.dispatch,
                    this.getState as () => RootState,
                );

            // Every checkpoint in this call shares the turn's action group so one undo reverts
            // the whole change; left to default, filter and intensity land as separate steps.
            const { parentActionGroup } = context;

            // Handle removal case
            if (remove) {
                setGlobalColorGradingFilter(null, false, parentActionGroup);
                return 'Success: Removed global color grading filter';
            }

            // Validate filter_name is provided if not removing
            if (!filter_name) {
                return `Error: filter_name is required. Available filters: ${listColorGradingFilterNames(activeFilters)}`;
            }

            const selectedFilter = findColorGradingFilterByName(activeFilters, filter_name);
            if (!selectedFilter) {
                return unknownColorGradingFilterError(filter_name, activeFilters);
            }

            // Apply the filter
            setGlobalColorGradingFilter(selectedFilter.texture_url, false, parentActionGroup);

            // Set intensity if provided
            if (intensity !== undefined) {
                setGlobalColorGradingFilterIntensity(intensity, false, parentActionGroup);
                // The intensity operation only checkpoints after a shared debounce, which can land
                // after this turn or be swallowed by another edit, so record it now.
                this.checkpointUnderTurn(parentActionGroup);
                return `Success: Applied "${selectedFilter.name}" color grading filter with ${Math.round(intensity * 100)}% intensity`;
            } else {
                return `Success: Applied "${selectedFilter.name}" color grading filter`;
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting global color grading: ${errorMessage}`;
        }
    }
}
